import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormGroup, FormRadioGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { mapRefDataPickList, sanitizeHtml } from 'shared/utils/Helpers';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';

export const BoilerDesignDetails = React.forwardRef((props, ref) => {
  const {
    hazardLevel: savedHazardLevel = '',
    designPressure: savedDesignPressure = '',
    designTemperature: savedDesignTemperature = '',
    minVolume: savedMinVolume = '',
    maxVolume: savedMaxVolume = '',
    outputUnit: storedOutputUnit = '',
    outputValue: savedOutputValue = '',
  } = props.value || {};

  const [hazardLevel, setHazardLevel] = useState(savedHazardLevel);
  const [designPressure, setDesignPressure] = useState(savedDesignPressure);
  const [minVolume, setMinVolume] = useState(savedMinVolume);
  const [maxVolume, setMaxVolume] = useState(savedMaxVolume);
  const [designTemperature, setDesignTemperature] = useState(savedDesignTemperature);
  const [outputUnit, setOutputUnit] = useState(storedOutputUnit);
  const [outputValue, setOutputValue] = useState(savedOutputValue);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const REF_CONSTANTS = PlantDesignRefData.boiler;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.boiler;

  const hazardLevelOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.hazardLevelOptions]);
  const outputUnitOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.outputUnitOptions]);

  const validators = [
    {
      fieldName: 'hazardLevel',
      fieldValue: hazardLevel,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'designPressure',
      fieldValue: designPressure,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'designTemperature',
      fieldValue: designTemperature,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'minVolume',
      fieldValue: minVolume,
      isValid: (v) => isEmpty(v) || isNumeric(v),
      errorMessage: () => 'Please enter only numeric value',
    },
    {
      fieldName: 'maxVolume',
      fieldValue: maxVolume,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'outputUnit',
      fieldValue: outputUnit,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'outputValue',
      fieldValue: outputValue,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        const isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isCurrentComponentValid);
        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    const details = {
      hazardLevel,
      designPressure,
      minVolume,
      maxVolume,
      designTemperature,
      outputUnit,
      outputValue,
    };
    props.onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [hazardLevel, designPressure, minVolume, maxVolume, designTemperature, outputUnit, outputValue]);

  useEffect(() => {
    props.onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <FormRadioGroup
        id="hazardLevel"
        name="hazardLevel"
        legend={PAGE_CONSTANTS.hazardLevel.label}
        helpMessage="Boiler hazard level according to AS 4343"
        options={hazardLevelOptions}
        vertical={true}
        onChange={setHazardLevel}
        value={hazardLevel}
        errorMessage={errors?.hazardLevel?.errorMessage}
        hasError={showErrors && errors?.hazardLevel?.invalid}
      />

      <FormGroup
        id="minVolume"
        label={PAGE_CONSTANTS.minVolume.label}
        isRequired
        margin={{ top: 'md' }}
        errorMessage={errors?.minVolume?.errorMessage}
        hasError={showErrors && errors?.minVolume?.invalid}
        className={styles.inputWithSuffix}
      >
        <Input
          suffix={PAGE_CONSTANTS.minVolume.suffix}
          onChange={(e) => {
            setMinVolume(e.target.value);
          }}
          value={minVolume}
        />
      </FormGroup>

      <FormGroup
        id="maxVolume"
        label={PAGE_CONSTANTS.maxVolume.label}
        isRequired
        errorMessage={errors?.maxVolume?.errorMessage}
        hasError={showErrors && errors?.maxVolume?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxVolume.suffix}
          onChange={(e) => setMaxVolume(e.target.value)}
          value={maxVolume}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="designTemperature"
        label={PAGE_CONSTANTS.designTemperature.label}
        isRequired
        errorMessage={errors?.designTemperature?.errorMessage}
        hasError={showErrors && errors?.designTemperature?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          suffix={sanitizeHtml(PAGE_CONSTANTS.designTemperature.suffix)}
          onChange={(e) => setDesignTemperature(e.target.value)}
          value={designTemperature}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="designPressure"
        label={PAGE_CONSTANTS.designPressure.label}
        isRequired
        errorMessage={errors?.designPressure?.errorMessage}
        hasError={showErrors && errors?.designPressure?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.designPressure.suffix}
          onChange={(e) => setDesignPressure(e.target.value)}
          value={designPressure}
          inputWidth="md"
        />
      </FormGroup>

      <FormRadioGroup
        id="outputUnit"
        name="outputUnit"
        legend={PAGE_CONSTANTS.outputUnit.label}
        options={outputUnitOptions}
        onChange={setOutputUnit}
        value={outputUnit}
        errorMessage={errors?.outputUnit?.errorMessage}
        hasError={showErrors && errors?.outputUnit?.invalid}
      />
      {outputUnit && (
        <FormGroup
          id="outputValue"
          label=""
          isRequired
          errorMessage={errors?.outputValue?.errorMessage}
          hasError={showErrors && errors?.outputValue?.invalid}
          className={styles.inputWithSuffix}
        >
          <Input
            suffix={outputUnit}
            onChange={(e) => setOutputValue(e.target.value)}
            value={outputValue}
            inputWidth="md"
          />
        </FormGroup>
      )}
    </>
  );
});

BoilerDesignDetails.displayName = 'BoilerDesignDetails';

BoilerDesignDetails.propTypes = {
  value: PropTypes.object,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
